import { useEffect, useRef, useState } from "preact/hooks";

import { api, audioAssetPath } from "@/api/client";
import type { AudioSegment, Paper } from "@/api/types";
import {
  audioSegmentsFor,
  audioStageForPaper,
  effectiveListenPurpose,
  manifestFailures,
  normalizeListenMode,
  segmentsFrom,
  type ListenMode,
  type ListenPurpose,
} from "@/lib/audioPolicy";

import { useToast } from "./ui";

export { segmentsFrom };

export interface ListenCommand {
  id: string;
  purpose: ListenPurpose;
}

const LISTEN_MODE_OPTIONS: ReadonlyArray<{ value: ListenMode; label: string }> = [
  { value: "brief", label: "精简" },
  { value: "detail", label: "完整" },
  { value: "abstract", label: "摘要" },
  { value: "all", label: "全部" },
];

export function ListenModeSelect({
  label,
  value,
  disabled = false,
  onChange,
}: {
  label: string;
  value: ListenMode;
  disabled?: boolean;
  onChange: (mode: ListenMode) => void;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const selectedLabel = LISTEN_MODE_OPTIONS.find((option) => option.value === value)?.label ?? "精简";

  useEffect(() => {
    const dismiss = (event: PointerEvent): void => {
      if (open && event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (open && event.key === "Escape") {
        setOpen(false);
        trigger.current?.focus();
      }
    };
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const selectMode = (mode: ListenMode): void => {
    onChange(mode);
    setOpen(false);
    trigger.current?.focus();
  };

  return (
    <div class="listen-mode-select" ref={root}>
      <button
        type="button"
        ref={trigger}
        class="btn btn-ghost listen-mode-trigger"
        aria-label={`${label}：${selectedLabel}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
      >
        <span>{selectedLabel}</span>
        <svg class="listen-mode-chevron" aria-hidden="true" viewBox="0 0 12 8">
          <path d="M1 1.5 6 6.5l5-5" />
        </svg>
      </button>
      {open && (
        <div class="listen-mode-menu" role="listbox" aria-label={label}>
          {LISTEN_MODE_OPTIONS.map((option) => (
            <button
              type="button"
              key={option.value}
              role="option"
              aria-selected={option.value === value}
              onClick={() => selectMode(option.value)}
            >
              <span>{option.label}</span>
              <span class="listen-mode-check" aria-hidden="true">{option.value === value ? "✓" : ""}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function failed(value: string | undefined): boolean {
  return ["failed", "blocked", "error"].includes((value ?? "").toLowerCase());
}

export function AudioPlayer({
  paper,
  mode,
  command,
  onListenRequested,
  showStartButton = true,
}: {
  paper: Paper;
  mode?: ListenMode;
  command?: ListenCommand;
  onListenRequested?: (purpose: ListenPurpose) => void;
  showStartButton?: boolean;
}) {
  const toast = useToast();
  const stage = audioStageForPaper(paper);
  const selectedMode = normalizeListenMode(mode, stage);
  const [segments, setSegments] = useState<AudioSegment[]>([]);
  const [index, setIndex] = useState(0);
  const [objectUrl, setObjectUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [terminal, setTerminal] = useState({
    audio: false,
    summary: failed(stage === "review" ? paper.deep_summary_status : paper.summary_status),
    message: "",
  });
  const audio = useRef<HTMLAudioElement>(null);
  const lastCommand = useRef("");
  const startRef = useRef<(purpose?: ListenPurpose) => Promise<void>>(async () => undefined);

  useEffect(() => () => {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }, [objectUrl]);

  useEffect(() => {
    setTerminal({
      audio: false,
      summary: failed(stage === "review" ? paper.deep_summary_status : paper.summary_status),
      message: "",
    });
  }, [paper.id, paper.summary_status, paper.deep_summary_status, stage]);

  const loadSegment = async (list: AudioSegment[], nextIndex: number): Promise<void> => {
    const segment = list[nextIndex];
    const path = segment ? audioAssetPath(segment) : null;
    if (!path) throw new Error("音频地址不可用");
    const blob = await api.audioBlob(path);
    const nextUrl = URL.createObjectURL(blob);
    setObjectUrl((previous) => {
      if (previous) URL.revokeObjectURL(previous);
      return nextUrl;
    });
    setSegments(list);
    setIndex(nextIndex);
    window.setTimeout(() => { void audio.current?.play().catch(() => undefined); }, 0);
  };

  const start = async (purpose = effectiveListenPurpose(stage, selectedMode)): Promise<void> => {
    setBusy(true);
    try {
      const manifest = await api.audioManifest(paper.id, stage);
      const failure = manifestFailures(manifest, stage, purpose);
      setTerminal(failure);
      if (failure.audio || (purpose !== "abstract" && failure.summary)) {
        toast(failure.message || (failure.summary ? "总结生成失败，请手动重试" : "云端音频失败，请手动重试"), "error");
        return;
      }
      const ready = audioSegmentsFor(manifest, stage, purpose);
      if (!ready.length) {
        await api.prepareAudio(paper.id, stage);
        toast("音频正在云端准备，稍后再试");
        return;
      }
      await loadSegment(ready, 0);
    } catch (error) {
      toast(error instanceof Error ? error.message : "音频加载失败", "error");
    } finally {
      setBusy(false);
    }
  };
  startRef.current = start;

  useEffect(() => {
    if (!command || command.id === lastCommand.current) return;
    lastCommand.current = command.id;
    void startRef.current(command.purpose);
  }, [command?.id, command?.purpose]);

  const requestStart = (): void => {
    const purpose = effectiveListenPurpose(stage, selectedMode);
    if (onListenRequested) onListenRequested(purpose);
    else void start(purpose);
  };

  const next = async (): Promise<void> => {
    if (index + 1 >= segments.length) return;
    setBusy(true);
    try { await loadSegment(segments, index + 1); }
    catch (error) { toast(error instanceof Error ? error.message : "音频加载失败", "error"); }
    finally { setBusy(false); }
  };

  const retryAudio = async (): Promise<void> => {
    setBusy(true);
    try {
      await api.retryAudio(paper.id, stage);
      setTerminal((current) => ({ ...current, audio: false, message: "" }));
      toast("音频已重新排队", "success");
    } catch (error) { toast(error instanceof Error ? error.message : "音频重试失败", "error"); }
    finally { setBusy(false); }
  };

  const retrySummary = async (): Promise<void> => {
    setBusy(true);
    try {
      if (stage === "review") await api.retryDeepSummary(paper.id);
      else await api.retryAudio(paper.id, stage);
      setTerminal((current) => ({ ...current, summary: false, message: "" }));
      toast("总结已重新排队", "success");
    } catch (error) { toast(error instanceof Error ? error.message : "总结重试失败", "error"); }
    finally { setBusy(false); }
  };

  if (!showStartButton && !busy && !objectUrl && !terminal.audio && !terminal.summary && !terminal.message) return null;

  return (
    <div class="audio-player" role="group" aria-label={`${paper.title} 听读控制`}>
      {showStartButton && <button type="button" class="btn btn-ghost" disabled={busy} onClick={() => requestStart()}>{busy ? "准备中…" : "听读"}</button>}
      {!showStartButton && busy && !objectUrl && <span aria-live="polite">正在准备音频…</span>}
      {objectUrl && (
        <>
          <audio
            ref={audio}
            src={objectUrl}
            controls
            onEnded={() => { if (index + 1 < segments.length) void next(); }}
          />
          <span aria-live="polite">音轨 {index + 1}/{segments.length}</span>
          {index + 1 < segments.length && (
            <button type="button" class="btn btn-ghost" disabled={busy} onClick={() => void next()}>下一段</button>
          )}
        </>
      )}
      {terminal.audio && <button type="button" class="btn btn-ghost audio-retry" disabled={busy} onClick={() => void retryAudio()}>重试音频</button>}
      {terminal.summary && <button type="button" class="btn btn-ghost audio-retry" disabled={busy} onClick={() => void retrySummary()}>{stage === "review" ? "重试全文总结" : "重试初筛总结"}</button>}
      {terminal.message && <span class="audio-error" role="alert">{terminal.message}</span>}
    </div>
  );
}
